import React, { useState, useEffect } from 'react';
import { instancesAPI } from '../../services/api';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import { useConfirm } from '../../hooks/useConfirm';
import { 
  Zap, 
  RefreshCw, 
  PowerOff, 
  Phone, 
  XCircle, 
  Activity, 
  Bot, 
  Search, 
  Trash2 
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

const AdminManagement = () => {
  const { darkMode } = useTheme();
  const showConfirm = useConfirm();
  const [loading, setLoading] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const handleRestart = async () => {
    if (window.confirm('Reiniciar instância?')) {
      setLoading(true);
      try {
        const response = await instancesAPI.restart(user.cliente_id);
        if (response.data.success) {
          toast.success('Instância reiniciada!');
        } else {
          toast.error(response.data.message || 'Erro ao reiniciar');
        }
      } catch (error) {
        toast.error('Erro ao reiniciar instância');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleReset = async () => {
    if (
      window.confirm(
        'Resetar instância travada? Isso irá limpar a conexão atual.'
      )
    ) {
      setLoading(true);
      try {
        const response = await instancesAPI.resetInstance(user.cliente_id);
        if (response.data.success) {
          toast.success('Instância resetada! Tente conectar novamente.');
        } else {
          toast.error(response.data.message || 'Erro ao resetar');
        }
      } catch (error) {
        toast.error('Erro ao resetar instância');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleDisconnect = async () => {
    if (window.confirm('Tem certeza que deseja desconectar o WhatsApp?')) {
      setLoading(true);
      try {
        toast.info('Desconectando WhatsApp...');

        const response = await instancesAPI.disconnectAPI(user.cliente_id);

        if (response.data.success) {
          toast.success('WhatsApp desconectado com sucesso!');
        } else {
          toast.warning(response.data.message || 'Desconexão com problemas');
        }
      } catch (error) {
        console.error('Erro ao desconectar:', error);
        toast.error(
          'Erro: ' + (error.response?.data?.message || error.message)
        );
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className={`text-2xl font-bold flex items-center ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <Zap className="mr-2" />
              Ações de Gerenciamento
            </h1>
            <p className={`mt-1 text-sm ${
              darkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              Ferramentas avançadas de administração do sistema
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Controle da Conexão */}
          <div className={`rounded-lg p-6 ${
            darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Controle da Conexão
            </h3>
            <div className="space-y-3">
              <button
                onClick={handleRestart}
                disabled={loading}
                className="w-full px-4 py-3 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center justify-center transition-colors"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Reiniciando...' : 'Reiniciar Instância'}
              </button>
              
              <button
                onClick={handleDisconnect}
                disabled={loading}
                className="w-full px-4 py-3 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center transition-colors"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                ) : (
                  <PowerOff className="w-4 h-4 mr-2" />
                )}
                {loading ? 'Desconectando...' : 'Desconectar WhatsApp'}
              </button>
              
              <button
                onClick={handleReset}
                disabled={loading}
                className="w-full px-4 py-3 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 disabled:opacity-50 flex items-center justify-center transition-colors"
                title="Resetar instância travada"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reset de Emergência
              </button>
            </div>
          </div>

          {/* Diagnóstico do Sistema */}
          <div className={`rounded-lg p-6 ${
            darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Diagnóstico do Sistema
            </h3>
            <div className="space-y-3">
              <button
                onClick={async () => {
                  try {
                    const response = await instancesAPI.getBrowserStatus();
                    const data = response.data?.data || response.data;
                    const message = data?.status === 'online'
                      ? `🟢 Browser Online\n${data.sessions || 0} sessões WhatsApp\n${data.tabs || 0} abas abertas`
                      : `🔴 Browser Offline\n${data?.error || 'Erro desconhecido'}`;
                    toast.info(message);
                  } catch (error) {
                    toast.error('Erro ao verificar browser');
                  }
                }}
                className="w-full px-4 py-3 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center transition-colors"
              >
                <Activity className="w-4 h-4 mr-2" />
                Status Browser
              </button>
              
              <button
                onClick={async () => {
                  try {
                    const response = await instancesAPI.getListenerStatus(user.cliente_id);
                    const data = response.data?.data || response.data;
                    const message = data?.ativo
                      ? `🟢 Listener Ativo\n${data.listeners || 0} listeners\nNúmero: ${data.numero || 'N/A'}`
                      : `🔴 Listener Inativo\n${data?.motivo || data?.erro || 'Motivo desconhecido'}`;
                    toast.info(message);
                  } catch (error) {
                    toast.error('Erro ao verificar listener');
                  }
                }}
                className="w-full px-4 py-3 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center transition-colors"
              >
                <Bot className="w-4 h-4 mr-2" />
                Status Listener
              </button>
            </div>
          </div>

          {/* Gerenciar Sessões */}
          <div className={`rounded-lg p-6 ${
            darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Gerenciar Sessões
            </h3>
            <div className="space-y-3">
              <button
                onClick={async () => {
                  if (
                    window.confirm(
                      '⚠️ Limpar sessão atual?\n\nIsso desconectará o WhatsApp e você precisará escanear o QR Code novamente.'
                    )
                  ) {
                    try {
                      await instancesAPI.clearSession(user.cliente_id);
                      toast.success('✅ Sessão limpa com sucesso!');
                    } catch (error) {
                      toast.error('❌ Erro ao limpar sessão');
                    }
                  }
                }}
                className="w-full px-4 py-3 text-sm bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 flex items-center justify-center transition-colors"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Limpar Sessão Atual
              </button>
              
              <button
                onClick={async () => {
                  try {
                    const response = await instancesAPI.listActiveSessions();
                    const data = response.data?.data || response.data;
                    const sessions = data?.mongodb || data || [];
                    const browser = data?.browser || {};

                    if (sessions.length === 0) {
                      toast.info('ℹ️ Nenhuma sessão ativa encontrada');
                    } else {
                      const message = `📊 Sessões Ativas:\n\nMongoDB: ${sessions.length} sessões\nBrowser: ${browser.status || 'N/A'} (${browser.sessions || 0} WhatsApp)`;
                      toast.info(message);
                      console.log('Sessões ativas:', { sessions, browser });
                    }
                  } catch (error) {
                    toast.error('❌ Erro ao listar sessões');
                  }
                }}
                className="w-full px-4 py-3 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center transition-colors"
              >
                <Search className="w-4 h-4 mr-2" />
                Listar Sessões Ativas
              </button>
            </div>
          </div>

          {/* Ações Avançadas */}
          <div className={`rounded-lg p-6 ${
            darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
            <h3 className={`text-lg font-semibold mb-4 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              Ações Avançadas
            </h3>
            <div className="space-y-3">
              <button
                onClick={async () => {
                  if (
                    window.confirm(
                      '🚨 ATENÇÃO: Limpar TODAS as sessões?\n\nIsso pode afetar outras conexões ativas no sistema. Esta ação é irreversível.'
                    )
                  ) {
                    try {
                      await instancesAPI.clearAllSessions();
                      toast.success('✅ Todas as sessões foram limpas!');
                    } catch (error) {
                      toast.error('❌ Erro ao limpar sessões');
                    }
                  }
                }}
                className="w-full px-4 py-3 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center transition-colors"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Limpar Todas as Sessões
              </button>
              
              <button
                onClick={async () => {
                  try {
                    await instancesAPI.reconnectInstance(user.cliente_id);
                    toast.success('🔄 Reconexão iniciada!');
                  } catch (error) {
                    toast.error('❌ Erro ao reconectar');
                  }
                }}
                className="w-full px-4 py-3 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center transition-colors"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Forçar Reconexão
              </button>
            </div>
          </div>
        </div>

        {/* Informações de Segurança */}
        <div className={`rounded-lg p-6 ${
          darkMode ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'
        }`}>
          <h3 className={`text-lg font-semibold mb-2 ${
            darkMode ? 'text-red-400' : 'text-red-800'
          }`}>
            ⚠️ Aviso de Segurança
          </h3>
          <p className={`text-sm ${
            darkMode ? 'text-red-300' : 'text-red-700'
          }`}>
            Estas são ferramentas avançadas de administração. Use com cuidado, pois algumas ações podem afetar 
            a conectividade do sistema e exigir reconfiguração. Sempre confirme antes de executar ações destrutivas.
          </p>
        </div>

        {/* Informações da Conta WhatsApp Business */}
        <WhatsAppAccountInfo darkMode={darkMode} clienteId={user.cliente_id} />
      </div>
    </Layout>
  );
};

// Componente para mostrar info da conta WhatsApp Business
const WhatsAppAccountInfo = ({ darkMode, clienteId }) => {
  const [accountInfo, setAccountInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [instancias, setInstancias] = useState([]);

  useEffect(() => {
    loadInstancias();
  }, [clienteId]);

  const loadInstancias = async () => {
    try {
      const res = await instancesAPI.list(clienteId);
      const whatsappInstances = (res.data || []).filter(i => i.platform === 'whatsapp' && i.provider === 'meta-api');
      setInstancias(whatsappInstances);
    } catch (error) {
      console.error('Erro ao carregar instâncias:', error);
    }
  };

  const loadAccountInfo = async (instanciaId) => {
    setLoading(true);
    try {
      const res = await instancesAPI.getAccountInfo(instanciaId);
      setAccountInfo(res.data.data);
      toast.success('Informações carregadas');
    } catch (error) {
      toast.error('Erro ao carregar informações da conta');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (instancias.length === 0) return null;

  return (
    <div className={`rounded-lg p-6 ${darkMode ? 'bg-zinc-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
      <h3 className={`text-lg font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
        Informações da Conta WhatsApp Business
      </h3>
      
      <div className="space-y-4">
        <select
          onChange={(e) => e.target.value && loadAccountInfo(e.target.value)}
          className={`w-full px-4 py-2 rounded-lg border ${
            darkMode ? 'bg-zinc-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
          }`}
        >
          <option value="">Selecione uma conta</option>
          {instancias.map(inst => (
            <option key={inst.id} value={inst.id}>
              {inst.numero_whatsapp || inst.nome_instancia}
            </option>
          ))}
        </select>

        {loading && (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
          </div>
        )}

        {accountInfo && !loading && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Phone Info */}
            <div className={`p-4 rounded-lg ${darkMode ? 'bg-zinc-700' : 'bg-gray-50'}`}>
              <h4 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Número
              </h4>
              <div className={`space-y-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                <p><strong>Número:</strong> {accountInfo.phone.number}</p>
                <p><strong>Nome verificado:</strong> {accountInfo.phone.verified_name}</p>
                <p><strong>Quality Rating:</strong> <span className={
                  accountInfo.phone.quality_rating === 'GREEN' ? 'text-green-500' :
                  accountInfo.phone.quality_rating === 'YELLOW' ? 'text-yellow-500' :
                  'text-red-500'
                }>{accountInfo.phone.quality_rating}</span></p>
                <p><strong>Limite de mensagens:</strong> {accountInfo.phone.messaging_limit_tier}</p>
              </div>
            </div>

            {/* WABA Info */}
            {accountInfo.waba && (
              <div className={`p-4 rounded-lg ${darkMode ? 'bg-zinc-700' : 'bg-gray-50'}`}>
                <h4 className={`font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                  WhatsApp Business Account
                </h4>
                <div className={`space-y-1 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  <p><strong>Nome:</strong> {accountInfo.waba.name}</p>
                  <p><strong>Moeda:</strong> {accountInfo.waba.currency}</p>
                  <p><strong>Timezone:</strong> {accountInfo.waba.timezone_id}</p>
                  <p><strong>Namespace:</strong> <span className="text-xs break-all">{accountInfo.waba.message_template_namespace}</span></p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminManagement;